import React, { createContext, useState, useRef, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useLocalSettings } from './LocalSettings';
import { HEADERS_HEIGHT } from '../../utils/headers';
import logo from "../../lib/assets/img/mobileLogo.png";
import { Button } from 'react-bootstrap';
import { compareValues, getBaseUrl, getDomainUrl, getMotdTooltipText, getPathUrl, setLoggedInUser } from '../../utils/general';
import Cookies from 'js-cookie';

export const silentLogin = async () => {
    const response = await fetch(`${getBaseUrl()}/login?nocache=${Date.now()}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            "Content-Type": "application/json",
            "mmlogin": true,
        },
    });
    if (!response.ok) throw new Error(`[${response.status}] ${response.url}`);
    return await response.json();
};

const EXTERNAL_AUTH_REDIRECTS = [
  "spacecockpit",
  "sewol"
];

export const checkExternalRedirect = (params, token) => {
    for(const redirectName of EXTERNAL_AUTH_REDIRECTS) {
      if(params.get("origin")?.includes(redirectName)) {
        params.delete("origin"); // keep query params passed to SBMS from SC except for origin
        params.set("token", token);
        globalThis.location = `${getDomainUrl(redirectName)}/?${params.toString()}`;
        return true;
      }
    }
    return false;
}

// Create and export the context
const AppState = createContext({});
export { AppState };

export const getType = (eventClass) => {
    if (!eventClass) return "unknown";
    
    const eventClassLower = eventClass.toLowerCase();
    
    // Handle RPO first as it's most specific
    if (eventClassLower.includes("rpo")) {
        return "rpo";
    }
    
    // Then handle other types
    if (eventClassLower.includes("maneuver") || eventClassLower.includes("manoeuvre") || eventClassLower.includes("period")) {
        return "maneuver";
    }
    if (eventClassLower.includes("photometry") || eventClassLower.includes("photometric")) {
        return "photometric";
    }
    if (eventClassLower.includes("launch")) {
        return "launch";
    }
    if (eventClassLower.includes("breakup")) {
        return "breakup";
    }
    
    return "unknown";
};

export const getCardColor = (type) => {
    if (!type) return 'secondary';
    
    const typeLower = type.toLowerCase();
    if (typeLower.includes('rpo')) return 'danger';
    if (typeLower.includes('maneuver') || typeLower.includes('manoeuvre')) return 'warning';
    if (typeLower.includes('photometry') || typeLower.includes('photometric')) return 'success';
    if (typeLower.includes('launch')) return 'info';
    return 'secondary';
};

// `silent` suppresses the error toast for background/polling callers, which would
// otherwise raise a fresh toast on every tick while an endpoint is down.
const getData = async (path, customHeaders = {}, {silent = false} = {}) => {
    try {
        const headers = {
            "Content-Type": "application/json",
            // Access token is automatically added from httpOnly cookie
            ...customHeaders
        };

        const response = await fetch(`${getBaseUrl()}/${path}`, {
            method: 'GET',
            credentials: 'include',
            headers: headers,
        });

        if (!response.ok) {
            // For guest users, return empty data for certain endpoints
            if (response.status === 401 && path === 'apps') {
                return [];
            }
            throw new Error(`Failed to fetch ${path}: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('API Error:', error);
        let errorMessage = 'An error occurred while fetching data. ';
        const toastOptions = { autoClose: 5000 };

        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            errorMessage += 'Unable to connect to the server. Please check your internet connection and try again.';
            // Shared id so every unreachable poll/call collapses to one toast.
            toastOptions.toastId = 'api-unreachable';
        } else {
            errorMessage += error.message;
            // Keyed on the endpoint so a repeatedly failing request replaces its
            // own toast instead of stacking a new one per attempt.
            toastOptions.toastId = `get-data-error:${path}`;
        }

        // Don't show error toast for guest users accessing apps endpoint
        if (!silent && !(error.message.includes('401') && path === 'apps')) {
            toast.error(errorMessage, toastOptions);
        }
        return null;
    }
};

const postLogin = async (username, password) => {
    if(!username || !password) return;
    return await fetch(`${getBaseUrl()}/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            "Content-Type": "application/json",
            "username": username,
            "password": password,
          },
    });
}

const postData = async (endpoint, data, method = "POST", customHeaders = {}, returnBadResponse=false) => {
    try {
        const response = await fetch(`${getBaseUrl()}/${endpoint}`, {
            method: method,
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            body: JSON.stringify(data),
        });

        if (!response.ok && response.status !== 200) {
            let error;
            if(response.headers.get("content-type")?.includes("application/json")) {
                error = await response.json();
            }

            if (returnBadResponse) {
                return {response, isError: true, error};
            }
            throw new Error(`Failed to ${method.toLowerCase()} ${endpoint}: ${error?.message ?? response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error('API Error:', error);
        let errorMessage = 'An error occurred while processing your request. ';
        const toastOptions = { autoClose: 5000 };

        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            errorMessage += 'Unable to connect to the server. Please check your internet connection and try again.';
            toastOptions.toastId = 'api-unreachable';
        } else {
            errorMessage += error.message;
        }

        toast.error(errorMessage, toastOptions);
        return null;
    }
};

const postDataThrow = async (endpoint, data, method = "POST", customHeaders = {}) => {
    const response = await postData(endpoint, data, method, customHeaders, true);

    if(!response || response.isError) {
        throw new Error(response?.error?.message ?? "Request failed");
    }

    return await response.json();
};

const fetchUserDetails = async (user, getData, setUserDetails, callback) => {
    if (!user.loggedIn || user.isGuest) return;
    
    const details = await getData("user-details");
    setUserDetails(details);

    if(callback) callback(details);
};

const fetchExercises = async (user, getData, setExercises, setLoading, silent = false) => {
    if (!user.loggedIn || user.isGuest) return;

    if (!silent) setLoading(true);

    try {
        // Background polls stay quiet: getData suppresses its own toast when silent.
        const data = await getData('exercises', {}, {silent});

        // getData swallows request failures and returns null (it has already surfaced
        // the error itself), so guard before spreading rather than throwing a TypeError.
        if (!data) {
            if (!silent) setExercises([]);
            return;
        }

        const exercises = [
            ...(data.groups ?? []),
            ...(data.invited ?? []).map((g) => ({...g, isInvited: true}))
        ];
        setExercises(exercises);
        onExercisesInit(exercises);
    } catch (error) {
        // Only unexpected errors reach here now (request failures come back as null
        // above), so this toast no longer doubles up with getData's.
        console.error('Error fetching exercises:', error);
        if (!silent) {
            toast.error("Failed to fetch exercises", {toastId: "fetch-exercises-error"});
            // Reset state on error
            setExercises([]);
        }
    } finally {
        if (!silent) setLoading(false);
    }
};

const fetchUserGroups = async (user, getData, setUserGroups) => {
    if (!user.loggedIn || user.isGuest) return;

    try {
        const data = await getData('user-groups');
        if (data) {
            setUserGroups([...data.groups, ...data.invited]);
        }
    } catch (error) {
        console.error('Error fetching user groups:', error);
        setUserGroups([]);
    }
};

export const sendMotdToasts = (motds) => {
    const toastMotds = motds?.filter((motd) => motd.ShowToast) ?? [];
    for (const motd of toastMotds) {
        toast.info(() => <div title={getMotdTooltipText(motd)}>
            {motd.Title}
        </div>, {
            position: "bottom-right",
            autoClose: 10000,
            pauseOnHover: true,
            pauseOnFocusLoss: true,
            hideProgressBar: false,
            closeOnClick: true,
        });
    }
};

const notifyLaunchDetections = (launchDetections) => {
    const onClick = () => {
        const url = new URL(getPathUrl(`/launch-activity`));
        window.open(url.toString(), '_blank', 'noopener,noreferrer')?.focus();
    };

    let notified = new Set();
    try {
        notified = new Set(JSON.parse(localStorage.getItem("notifiedLaunchDetections")));
    } catch (e) {
        console.warn("notifiedLaunchDetections parse", e);
    }

    let sentNotify = false;

    for (const detection of launchDetections) {
        if((detection.Type === "prediction" || detection.ChangeType === "added" || detection.ChangeType === "removed") && !notified.has(detection.IdOriginal)) {
            notified.add(detection.IdOriginal);
            sentNotify = true;
            
            toast.warning(() => <div>
                {detection.Events?.[0]?.descriptor ?? detection.Summary}
                <br />
                <Button variant="outline-warning" size="sm" style={{marginTop: "10px"}}
                    onClick={onClick}
                >View Launch {detection.Type === "prediction" ? "Prediction" : "Detection"}</Button>
            </div>, {
                position: "bottom-right",
                autoClose: 10000,
                pauseOnHover: true,
                pauseOnFocusLoss: true,
                hideProgressBar: false,
                closeOnClick: true,
            });
        }
    }

    if(sentNotify) {
        localStorage.setItem("notifiedLaunchDetections", JSON.stringify([...notified]));
    }
};

// Fetch the satellite catalog and normalize it to the shape the app expects. Returns null on
// invalid responses so callers can decide whether to overwrite existing data.
const fetchSatellites = async (getData, setSatellitesRefreshing) => {
    try {
        setSatellitesRefreshing(true);
        const satData = await getData("satellites", {names_only: "no"});
        setSatellitesRefreshing(false);

        if (!satData || !Array.isArray(satData)) {
            console.error({
                message: "Invalid satellite data received",
                data: satData,
                time: new Date(),
            });
            return null;
        }

        return satData.filter(Boolean).map(sat => {
            // If the data is already in the correct format, return it as is
            if (typeof sat === 'object' && sat.id && sat.name) {
                return sat;
            }

            return {
                ...sat,
                id: sat.SatNo+"", name: sat.CommonName ?? "Unknown", // backwards compat with everywhere satellites is currently used
            };
        });
    } catch (e) {
        setSatellitesRefreshing(false);
        console.error("Error retrieving satellites:", e);
        return null;
    }
};



const detectionTypeNames = {
    "ShipLaunch": "Ship Launch",
    "ShipTracking": "Ship Tracking",
    "rocket": "Rocket"
};

const getDetectionTypes = (events) => {
    return [...new Set(events.flatMap((e) => 
        e?.detections?.objects?.map((d) => detectionTypeNames[d.className] ?? d.className) ?? []
    ).filter(Boolean))];
};

const getDetectionCategories = (detection) => {
    const detections = getDetectionTypes(detection.Events ?? []);
    const prevDetections = getDetectionTypes(detection.PreviousEvents ?? []);

    const categories = new Set([
        ...detections.map((d) => d + " Detected"),
        ...prevDetections.map((d) => d + " Previously Detected"),
    ]);

    if(!categories.size) categories.add("No Detections");

    return categories;
};

const processLaunchDetections = (launchDetections) => {
    const filtered = launchDetections
        .filter((detection) => detection?.Events?.length) // filter out ones with no current events
        .map((detection) => {
            detection.Type = "monitoring";
            
            if(detection.PreviousEvents?.length) {
                detection.PreviousEvents.sort((a, b) => compareValues(a?.availableImage?.acquisitionTime, b?.availableImage?.acquisitionTime, false)); // newest to oldest
                detection.AcquiredAt = detection.PreviousEvents[0]?.availableImage?.acquisitionTime;
            }

            if(detection.Events?.length) {
                detection.Events.sort((a, b) => compareValues(a?.availableImage?.acquisitionTime, b?.availableImage?.acquisitionTime, false)); // newest to oldest
                detection.AcquiredAt = detection.Events[0]?.availableImage?.acquisitionTime;
            }

            detection.DetectionCategories = getDetectionCategories(detection);

            return detection;
        });
    return filtered;
};

const fetchOtherData = async (user, getData, setSatellites, setMotds, setLaunchDetections, setLaunchPredictions, setSatellitesRefreshing) => {
    if (!user.loggedIn) return;
    const [formattedSats, motds, launchDetections, launchPredictions] = await Promise.all([
        fetchSatellites(getData, setSatellitesRefreshing),
        getData("motd/list"),
        getData("launch-detection"),
        getData("launch-predictions")
    ]);

    if (formattedSats) {
        setSatellites(formattedSats);
    }

    if (Array.isArray(motds)) {
        setMotds(motds);
        sendMotdToasts(motds);
    } else {
        console.error("Could not retrieve messages of the day:", motds);
    }

    if (Array.isArray(launchDetections)) {
        const filtered = processLaunchDetections(launchDetections);
        setLaunchDetections(filtered);
        
        const toNotify = [...filtered];
        if(Array.isArray(launchPredictions)) {
            launchPredictions.forEach((p) => {
                p.Type = "prediction";
                p.AcquiredAt = p.CreatedAt;
                if(p.Message?.evidence || p.Message?.supporting_evidence) {
                    p.Message.evidence = [
                        ...(p.Message.evidence ?? []),
                        ...(p.Message.supporting_evidence ?? [])
                    ];
                }

                const location = p.Message?.location?.complex_name;
                if(location?.length) p.Complex = location;
                p.SpaceportAtlasCode = p.Message?.location?.complex_spa_code ?? null;
                
                p.Summary = p.Message?.summary ?? "";

                if(!p.Summary?.length) {
                    p.Summary = "Launch predicted";
                    
                    if(location?.length) {
                        p.Summary += " at " + location;
                    }
                }
            });
            setLaunchPredictions(launchPredictions);
            toNotify.push(...launchPredictions);
        }

        notifyLaunchDetections(toNotify);
    } else if(launchDetections?.message?.includes("app installed")) {
        setLaunchDetections({needsApp: true});
    } else console.error("Could not retrieve launch detections:", launchDetections);
}

const patchData = async (endpoint, customHeaders = {}) => {
    try {
        const headers = {
            "Content-Type": "application/json",
            // Access token is automatically added from httpOnly cookie
            ...customHeaders
        };

        const response = await fetch(`${getBaseUrl()}/${endpoint}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: headers
        });

        if (!response.ok) {
            throw new Error(`Failed to patch ${endpoint}: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error('API Error:', error);
        let errorMessage = 'An error occurred while processing your request. ';
        const toastOptions = { autoClose: 5000 };

        if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
            errorMessage += 'Unable to connect to the server. Please check your internet connection and try again.';
            toastOptions.toastId = 'api-unreachable';
        } else {
            errorMessage += error.message;
        }

        toast.error(errorMessage, toastOptions);
        throw error;
    }
};

const exerciseSingleton = {
    promise: null,
    data: null
};

const fetchExerciseData = async () => {
    if (exerciseSingleton.data) {
        return exerciseSingleton.data;
    }
    if (!exerciseSingleton.promise) {
        exerciseSingleton.promise = getData("get-exercise");
    }
    const response = await exerciseSingleton.promise;
    exerciseSingleton.data = response;
    return response;
};

const onExercisesInit = (exercises) => {
    if(globalThis.exerciseAlarms) {
        try {
            for (const a of globalThis.exerciseAlarms) {
                clearTimeout(a);
            }
        } catch (error) {
            // Ignore errors when clearing stale exercise alarms
            console.debug('Failed to clear exercise alarms:', error);
        }
    }

    if(!exercises?.length) return;

    const alarms = exercises
        .flatMap((ex) => ex?.Events ?? [])
        .filter((ev) => ev?.Status === "PENDING" || ev?.Status === "OPEN")
        .flatMap((ev) => ev.Alarms?.map((a) => ({...a, event: ev})) ?? [])
        .filter((alarm) => alarm.enabled);

    const makeTimeout = (triggerTime, now, alarm) => {
        const deltaMs = triggerTime - now;
        return setTimeout(() => {
            const onClick = () => {
                const url = new URL(getPathUrl(`/home#event/${alarm.event.ExerciseId}/${alarm.event.Id}`));
                window.open(url.toString(), '_blank', 'noopener,noreferrer')?.focus();
            };

            toast.info(() => (
                <div>
                    Event: {alarm.event.Name}
                    <hr />{alarm.description}
                    <br /><Button className="mt-2" variant="outline-info" size="sm"
                        onClick={onClick}
                    >View</Button>
                </div>
                ), {
                position: "bottom-right",
                autoClose: false,
                pauseOnHover: true,
                pauseOnFocusLoss: true,
                hideProgressBar: false,
                closeOnClick: true,
                data: { isAlarm: true }
            });

            const notif = new Notification(
                alarm.event.Name, {
                    badge: logo, 
                    icon: logo,
                    body: alarm.description,
                    requireInteraction: true,
                    // tag allows it to replace duplicates instead of stacking it up when being sent from multiple tabs
                    tag: `ALARM_EX_${alarm.event.Id}_${alarm.id}` 
                }
            );
            notif.onclick = () => {
                onClick();
                notif.close();
            };
        }, deltaMs);
    };

    globalThis.exerciseAlarms = alarms.flatMap((alarm) => {
        const time = new Date(alarm.time).getTime();
        const now = Date.now();

        const timeouts = [];

        if(time >= now && (time - now) <= 24 * 60 * 60 * 1000) { 
            // in future. also don't set timeouts alarms more than a day out, since 24+ days causes integer overflow and immediate callback execution
            timeouts.push(makeTimeout(time, now, alarm));
        }

        return timeouts;
    });

};

const defaultSatConfig = {
    numSats: -1, dataType: "All", 
    satellites: [],
    skipSending: true,
    skipStatesQuery: true,
    nonTLEPickerConfig: true,
};

export const AppProvider = ({ children }) => {

    const [user, setUser] = useState(() => {
        return {
            loggedIn: false,
            uuid: null,
            access_token: null,
            providers: [],
            isGuest: false,
            isSuperuser: false,
            lastLoggedIn: -1
        };
    });

    const [scQueryParams, setSCQueryParams] = useState(null);

    const [authChecking, setAuthChecking] = useState(() => Boolean(Cookies.get('has_access_token')));
    const silentAuthAttempted = useRef(false);

    // Returns true if a full-page redirect was started, so callers can skip
    // work that the navigation would immediately throw away.
    const handleRedirect = useCallback((token) => {
        // check if we need to redirect to SC or home page
        // unlike handleRedirect in Login.jsx, it does not redirect if we're going to a specific page
        const params = new URLSearchParams(location.search);
        if(checkExternalRedirect(params, token)) return true;

        if (!location?.pathname?.length || location.pathname === "/") {
          const query = location.search?.length > 0 ? location.search : "";
          globalThis.location = '/home' + query;
          return true;
        }

        return false;
    }, []);

    useEffect(() => {
        if (silentAuthAttempted.current) return;
        if (!Cookies.get('has_access_token')) return;
        silentAuthAttempted.current = true;
        silentLogin()
            .then((data) => {
                // A full-page redirect reloads the app and re-runs the whole
                // login/data-fetch cycle, so don't mark the user logged in here:
                // doing so fires the data fetches (exercises, user-details, ...)
                // just for the navigation to cancel them and surface error toasts.
                // Keep authChecking true so the login form doesn't flash while
                // the browser navigates away.
                if (!handleRedirect(data.jwt)) {
                    setLoggedInUser(data, setUser);
                    setAuthChecking(false);
                }
            })
            .catch((err) => {
                console.error('Silent re-auth failed:', err);
                setAuthChecking(false);
            });
    }, [handleRedirect]);

    const pendingRedirect = useRef(null);
    
    const isLocalDev = globalThis.location.hostname === "localhost" || globalThis.location.hostname === "127.0.0.1";

    const [userDetails, setUserDetails] = useState(null);
    const [userDetailsDirty, setUserDetailsDirty] = useState(false);

    const [exercises, setExercises] = useState([]);
    const [exercisesLoading, setExercisesLoading] = useState(false);
    const [exercisesDirty, setExercisesDirty] = useState(false);

    const [userGroups, setUserGroups] = useState([]);

    const [currentDashDetails, setCurrentDashDetails] = useState(null);
    const [isShowingSC, setIsShowingSC] = useState(null);
    const [viewingExerciseEvent, setViewingExerciseEvent] = useState(null);

    const [satellites, setSatellites] = useState(null);
    const [satellitesRefreshing, setSatellitesRefreshing] = useState(false);
    const getSatName = useCallback((id) => {
        const lower = (id+"").toLowerCase();
        return satellites?.find((s) => (s.id+"").toLowerCase() === lower)?.name;
    }, [satellites]);

    // Manually re-fetch the satellite catalog (e.g. the Catalog dialog's refresh button) so an
    // already-open dialog can pick up satellites added on the backend since login.
    const refreshSatellites = useCallback(async () => {
        if (!user.loggedIn) return;
        
        const formattedSats = await fetchSatellites(getData, setSatellitesRefreshing);
        if (formattedSats) setSatellites(formattedSats);
    }, [user]);

    const [motds, setMotds] = useState(null);
    const [launchDetections, setLaunchDetections] = useState(null);
    const [launchPredictions, setLaunchPredictions] = useState(null);

    useEffect(() => {
        fetchUserDetails(user, getData, setUserDetails);
        fetchOtherData(user, getData, setSatellites, setMotds, setLaunchDetections, setLaunchPredictions, setSatellitesRefreshing);
        fetchExercises(user, getData, setExercises, setExercisesLoading);
        fetchUserGroups(user, getData, setUserGroups);
    }, [user]);

    useEffect(() => {
        if(userDetailsDirty) {
            fetchUserDetails(user, getData, setUserDetails, 
                (typeof userDetailsDirty) === "object" ? userDetailsDirty?.callback : null);
            setUserDetailsDirty(false);
        }
    }, [userDetailsDirty, user]);
    
    useEffect(() => {
        if(exercisesDirty) {
            fetchExercises(user, getData, setExercises, setExercisesLoading);
            setExercisesDirty(false);
        }
    }, [exercisesDirty, user]);

    // Poll for exercise/event updates other users may have made. Runs silently
    // so it doesn't flicker the loading spinner or clobber the list on a
    // transient failure. Events flow down as part of each exercise payload.
    useEffect(() => {
        if(!user?.loggedIn || user?.isGuest) return;
        const interval = setInterval(() => {
            fetchExercises(user, getData, setExercises, setExercisesLoading, true);
        }, 10_000);
        return () => clearInterval(interval);
    }, [user]);

    const [exercise, setExercise] = useState(null);
    const fetchedExercise = useRef(false);

    useEffect(() => {
        const loadExercise = async () => {
            if (!fetchedExercise.current) {
                fetchedExercise.current = true;
                const data = await fetchExerciseData();
                setExercise(data);
            }
        };
        if(user.loggedIn) {
            loadExercise();
        }
    }, [user]);

    const includeExerciseData = useMemo(() => userDetails?.Settings?.includeExerciseData === "Yes", [userDetails]);

    const groupLogo = useMemo(() => {
        if (!userDetails?.DataGroup || userDetails.DataGroup === "default" || !userGroups?.length) {
            return null;
        }
        // Normalize comparison: DataGroup may use underscores while Name may use spaces
        // Also check against DataGroupControl field which may match the user's DataGroup setting
        const normalizedDataGroup = userDetails.DataGroup.replaceAll("_", " ").toLowerCase();
        const activeGroup = userGroups.find((g) =>
            g.Name?.replaceAll("_", " ").toLowerCase() === normalizedDataGroup ||
            g.DataGroupControl?.replaceAll("_", " ").toLowerCase() === normalizedDataGroup
        );
        return activeGroup?.Logo || null;
    }, [userDetails, userGroups]);

    const [simTime, setSimTime] = useState(null);

    // DB-stored user settings
    const sourceFilters = useMemo(() => {
        const filters = userDetails?.Settings?.sourceFilters;
        if(filters) {
            try {
                const parsed = JSON.parse(filters);
                // Ensure we return an object, not an array
                if(typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
            } catch (error) {
                // Invalid JSON, return default empty object
                console.debug('Failed to parse local settings:', error);
            }
        }
        return {};
    }, [userDetails]);
   
    const {
        selectedSourceFilter, setSelectedSourceFilter,
        lastDashboard, setLastDashboard
    } = useLocalSettings(sourceFilters);

    const persistSatnos = useMemo(() => userDetails?.Settings?.persistentSatnos !== "No", [userDetails]);
    const [refreshLink, setRefreshLink] = useState(0);

    const scUseSBMSAssetPicker = useMemo(() => userDetails?.Settings?.scUseSBMSAssetPicker === "Yes", [userDetails]);

    const headersHeight = useMemo(() => {
        return HEADERS_HEIGHT(exercise, includeExerciseData, motds);
    }, [exercise, includeExerciseData, motds]);
    
    const globalSatConfigRef = useRef(defaultSatConfig);
    const [globalSatConfigState, setGlobalSatConfigState] = useState(defaultSatConfig);

    const updateGlobalSatConfig = useCallback((newState) => {
        const actualNewState = newState ?? defaultSatConfig;

        setGlobalSatConfigState(actualNewState);
        globalSatConfigRef.current = actualNewState;
    }, []);

    // if persist satnos is disabled, clear the globalSatConfigRef
    useEffect(() => {
        if(!persistSatnos) updateGlobalSatConfig(null);
    }, [persistSatnos, updateGlobalSatConfig]);

    useEffect(() => {
        try {
            // set skipSending to true so sat config doesn't override URL query params on initial load.
            // manual update of config always sets skipSending to false anyways
            const savedConfig = JSON.parse(localStorage.getItem("UISatConfig"));
            updateGlobalSatConfig((persistSatnos && savedConfig) ? {...savedConfig, skipSending: true} : null);
            setRefreshLink((r) => r + 1);
        } catch (e) {
            console.error('Error parsing localStorage UISatConfig:', e);
        }
    }, [persistSatnos, updateGlobalSatConfig]);

    const setGlobalSatConfig = useCallback((newVal) => {
        if(persistSatnos) {
            updateGlobalSatConfig(newVal);
            localStorage.setItem("UISatConfig", JSON.stringify(newVal));
        }
    }, [persistSatnos, updateGlobalSatConfig]);

    const store = useMemo(() => ({
        user,
        setUser,
        authChecking,
        pendingRedirect,
        isLocalDev,
        getBaseUrl,
        getData,
        postLogin,
        postData,
        postDataThrow,
        patchData,
        scQueryParams, setSCQueryParams,
        sourceFilters, selectedSourceFilter, setSelectedSourceFilter,
        lastDashboard, setLastDashboard,
        userDetails, setUserDetails,
        userDetailsDirty, setUserDetailsDirty,
        userGroups,
        satellites, getSatName,
        satellitesRefreshing, refreshSatellites,
        simTime, setSimTime,
        refreshLink, setRefreshLink, persistSatnos,
        exercise,
        includeExerciseData,
        headersHeight,
        exercises, setExercises,
        exercisesLoading,
        setExercisesDirty,
        motds, setMotds,
        launchDetections, setLaunchDetections,
        launchPredictions, setLaunchPredictions,
        currentDashDetails, setCurrentDashDetails,
        viewingExerciseEvent, setViewingExerciseEvent,
        isShowingSC, setIsShowingSC,
        groupLogo,
        globalSatConfigRef, globalSatConfigState, setGlobalSatConfig,
        scUseSBMSAssetPicker
    }), [
        user, authChecking, isLocalDev, pendingRedirect,
        scQueryParams, setSCQueryParams,
        sourceFilters, selectedSourceFilter, setSelectedSourceFilter,
        lastDashboard, setLastDashboard,
        userDetails, setUserDetails,
        userDetailsDirty, setUserDetailsDirty,
        userGroups,
        satellites, getSatName,
        satellitesRefreshing, refreshSatellites,
        simTime, setSimTime,
        refreshLink, setRefreshLink, persistSatnos,
        exercise,
        includeExerciseData,
        headersHeight,
        exercises, setExercises,
        exercisesLoading,
        setExercisesDirty,
        motds, setMotds,
        launchDetections, setLaunchDetections,
        launchPredictions, setLaunchPredictions,
        currentDashDetails, setCurrentDashDetails,
        viewingExerciseEvent, setViewingExerciseEvent,
        isShowingSC, setIsShowingSC,
        groupLogo,
        globalSatConfigRef, globalSatConfigState, setGlobalSatConfig,
        scUseSBMSAssetPicker
    ]);

    return (
        <AppState.Provider value={store}>      
            {children}
        </AppState.Provider>
    );
};

AppProvider.propTypes = {
    children: PropTypes.node.isRequired
};